<template>
	<fieldset>
		<div class="container pt-5 pb-3">
			<div class="row no-gutters">
				<div class="fieldset-title w-100">
					<h2 class="heading">Utility Costs</h2>
				</div>

				<div class="row no-gutters">
					<div class="col-12 w-100 mt-2">
						<p>
							Please upload a CSV file of utility pricing policy 
							<a href="/static/vue-dist/utility_cost_template.csv" download="utility_cost_template.csv" class="download-link">(download CSV template HERE)</a>
						</p>
					</div>
				</div>
				<div class="row no-gutters w-100">
					<div
						class="col-4 ml-5 d-flex flex-column justify-content-end align-items-start"
					>
						<label for="file-upload" class="custom-file-upload btn btn-box js-btn-expand csv-button px-5">
							Upload CSV
						</label>
						<input 
							id="file-upload" 
							type="file" 
							ref="fileInput"
							@change="handleFileUpload" 
							accept=".csv" 
							style="display: none;"
						/>
						<div v-if="fileName" class="file-name-display mt-2">
							{{ fileName }}
						</div>
		
						<div class="suffix-wrap"></div>
					</div>
				</div>

				<button
					type="button"
					v-if="csvUploaded"
					@click="cancelFileUpload"
					class="btn btn-secondary mt-2"
				>
					Cancel Upload
				</button>

				<div v-if="!csvUploaded" class="row no-gutters">
					<p>
						<b>OR</b> fill out information below:
					</p>
				</div>
			</div>
			<div v-if="!csvUploaded" class="w-full">

				<div class="row">
					<div style="z-index: 9999;">
						<FormInput dataKey="baseUtilityMonthlyCharges" />
					</div>
					
				</div>
				
				<div class="row">
					<div class="month-col flex items-center gap-1">
						<label class="text-center btn-help">Start Month</label>
						<Tooltip
							:title="inputConfig.start_month.label"
							align="right"
						>
							<p>
								{{ inputConfig.start_month.definition }}
							</p>
						</Tooltip>
					</div>
					<div class="month-col ml-4 flex items-center gap-1">
						<label class="text-center btn-help">End Month</label>
						<Tooltip
							:title="inputConfig.end_month.label"
							align="right"
						>
							<p>
								{{ inputConfig.end_month.definition }}
							</p>
						</Tooltip>
					</div>
					<div class="hour-col ml-4 flex gap-0">
						<label class="btn-help">Peak Hours</label>
						<Tooltip
							:title="inputConfig.peak_hours.label"
							align="right"
						>
							<p>
								{{ inputConfig.peak_hours.definition }}
							</p>
						</Tooltip>
					</div>
					<div class="unit-col ml-4 flex gap-0">
						<label class="btn-help">Peak Demand</label>
						<Tooltip
							:title="inputConfig.peak_demand.label"
							align="right"
						>
							<p>
								{{ inputConfig.peak_demand.definition }}
							</p>
						</Tooltip>
					</div>
					<div class="unit-col ml-4 flex gap-0">
						<label class="btn-help">Peak Energy</label>
						<Tooltip
							:title="inputConfig.peak_energy.label"
							align="right"
						>
							<p>
								{{ inputConfig.peak_energy.definition }}
							</p>
						</Tooltip>
					</div>
					<div class="unit-col-large ml-4 flex gap-0">
						<label class="btn-help">Off-Peak Demand</label>
						<Tooltip
							:title="inputConfig.off_peak_demand.label"
							align="left"
						>
							<p>
								{{ inputConfig.off_peak_demand.definition }}
							</p>
						</Tooltip>
					</div>
					<div class="unit-col ml-4 flex gap-0">
						<label class="btn-help">Off-Peak Energy</label>
						<Tooltip
							:title="inputConfig.off_peak_energy.label"
							align="left"
						>
							<p>
								{{ inputConfig.off_peak_energy.definition }}
							</p>
						</Tooltip>
					</div>
				</div>
				
				<PeriodRow
				v-for="(period, i) in formData.chargingPeriods"
				:key="i"
				:period="period"
				:update="(key, val) => formData.chargingPeriods.splice(i, 1, { ...period, [key]: val })"
				:removeLastPeriod="() => formData.chargingPeriods.pop()"
				:availableEndMonths="availableEndMonths"
				:isLast="i === formData.chargingPeriods.length - 1"
				/>
				
				<div class="row">
					<div :class="['fieldgroup', !canAddRow && 'disabled']">
						<button
						type="button"
						@click.prevent="addChargingPeriod"
							class="btn btn-box js-btn-expand w-[160px]"
							>
							<i class="fa-solid fa-plus mr-2 flex align-items-center justify-content-center" />
							Add Row
						</button>
					</div>
				</div>
			</div>
		</div>
	</fieldset>
</template>

<script>
import FormInput from '@/components/FormInput'
import formData from '@/components/Sizer/form-data-store'
import PeriodRow from './components/PeriodRow'
import Tooltip from '@/components/Tooltip'
import inputConfig from '@/components/FormInput/input-config'

export const emptyChargingPeriod = {
	startMonth: 'January',
	endMonth: undefined,
	peakHours: '17-19',
	peakDemand: 5.13,
	peakEnergy: 0.11,
	offPeakDemand: 0.31,
	offPeakEnergy: 0.05,
}

const months = [
	'January',
	'February',
	'March',
	'April',
	'May',
	'June',
	'July',
	'August',
	'September',
	'October',
	'November',
	'December',
]

export default {
	name: 'UtilityCalcInputs',

	components: {
		FormInput,
		PeriodRow,
		Tooltip
	},

	data: () => ({
		formData,
		selectedFile: null,
		fileName: '',
		csvUploaded: false,
		inputConfig
	}),

	computed: {
		lastPeriod () {
			const { chargingPeriods } = formData

			return chargingPeriods[chargingPeriods.length - 1]
		},

		availableEndMonths () {
			const { lastPeriod: { startMonth } } = this

			return months.slice(months.indexOf(startMonth))
		},

		canAddRow () {
			const { lastPeriod: { endMonth } } = this

			return endMonth && endMonth !== 'December'
		}
	},

	methods: {
		addChargingPeriod () {
			const { lastPeriod: { endMonth } } = this
			const startMonth = months[months.indexOf(endMonth) + 1]

			formData.chargingPeriods.push({
				...emptyChargingPeriod,
				startMonth,
			})
		},
		handleFileUpload(event) {
			const file = event.target.files[0];
			if (file) {
				this.fileName = file.name;
				this.selectedFile = file;
				this.csvUploaded = true;
				this.$emit('file-selected', file); // Emit event to parent component
			}
		},
		cancelFileUpload() {
			this.selectedFile = null;
			this.fileName = '';
			this.csvUploaded = false; 
			this.$refs.fileInput.value = null; // Reset the input element
			this.$emit('file-selected', null);
		},
	},
}
</script>

<style scoped>
.unit-col {
	width: 168px;
}
.unit-col-large {
	width: 175px;
}

.month-col {
	width: 138px;
}

.hour-col {
	width: 130px;
}

.csv-button {
	background-color: #2EA3F2;
	color: white;
}

.csv-button:hover {
	background-color: #0C7AC5;
}

.custom-file-upload {
    background-color: #2EA3F2;
	color: white;
}

.custom-file-upload:hover {
    background-color: #0C7AC5;
}

.file-name-display {
    font-size: 0.9rem;
    color: #555;
    margin-top: 5px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 200px;
}

</style>
